import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { forwardRef, memo, useContext, useMemo } from 'react';

import { LAYOUT_SIZE } from '@/hooks/useLayoutSize';
import useOpenMemoryDetail from '@/hooks/useOpenMemoryDetail';
import useStorage from '@/hooks/useStorage';
import type { ReactClassNameInterface } from '@/types/common';
import { isValidHttpUrl } from '@/utils/utils';

import ContextBasicInfo from '../../context/BasicInfo';

// Dynamic imports
const NoteItem = dynamic(() => import('@/components/Note/NoteItem'), { ssr: false });
const CitationLinkCard = dynamic(
  () => import('./components/template/LearnMore/CitationLinkCardItem/PopoverCitationCard'),
  { ssr: false }
);

// url 对应普通的citation
// detail 对应citationNote
export interface IProps extends ReactClassNameInterface {
  url?: string;
  detail?: string;
  linkInfo?: string;
}

const CitationPopover = forwardRef<HTMLDivElement, IProps>(function TaskToolBar(props, ref) {
  const { className, style, url, detail = '{}', linkInfo = '{}' } = props;
  const { isShared } = useContext(ContextBasicInfo);
  const [layoutSize] = useStorage('layoutSize');
  const { openMemoryDetail } = useOpenMemoryDetail(layoutSize === LAYOUT_SIZE.SMALL);

  const citationNote = useMemo(() => {
    try {
      const ret = JSON.parse(decodeURIComponent(detail));

      return ret;
    } catch (e) {
      console.log('citationNoteError', e);

      return {};
    }
  }, [detail]);

  const linkInfoObj = useMemo(() => {
    try {
      const ret = JSON.parse(decodeURIComponent(linkInfo));

      return ret;
    } catch (e) {
      console.log('citationLinkInfoError', e);

      return {};
    }
  }, [linkInfo]);

  const showLinkCard = !!linkInfoObj.title || !!linkInfoObj.summary;

  return (
    <div
      ref={ref}
      className={classNames(
        'px-4 py-2 rounded-[8px] flex justify-center items-center',
        url && !showLinkCard ? 'bg-[white] shadow-normal ' : 'bg-transparent',
        className
      )}
      style={{
        ...style,
        ...(isShared ? { cursor: 'auto' } : {})
      }}
    >
      {url ? (
        showLinkCard ? (
          <CitationLinkCard
            href={isValidHttpUrl(url) ? url : undefined}
            summary={linkInfoObj.summary || ''}
            title={linkInfoObj.title || ''}
          />
        ) : (
          <a
            className={classNames(
              'line-clamp-1 text-[#379FFF]',
              isValidHttpUrl(url) ? 'underline' : 'cursor-auto'
            )}
            href={isValidHttpUrl(url) ? url : undefined}
            rel="noreferrer"
            target="_blank"
          >
            {url}
          </a>
        )
      ) : (
        !!citationNote.noteId && (
          <NoteItem
            className="max-w-[270px] !bg-primary-white"
            detail={citationNote}
            onClick={() => {
              isShared
                ? null
                : openMemoryDetail({
                    noteId: citationNote.noteId
                  });
            }}
            origin="chat"
            showTime={false}
            style={{
              ...(isShared ? { cursor: 'auto' } : {})
            }}
          />
        )
      )}
    </div>
  );
});

CitationPopover.displayName = 'CitationPopover';

export default memo(CitationPopover);
